import "./App.css";
import { Grid, ThemeProvider } from "@mui/material";
import mainTheme from "./theme.tsx";
import CssBaseline from "@mui/material/CssBaseline";
import { useDispatch } from "react-redux";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useEffect, useLayoutEffect, useState, Fragment } from "react";
import {
  conversationActions,
  fetchConversations,
  fetchExchanges,
} from "./store/conversation.tsx";
import { fetchFeatureFlags } from "./store/featureFlags.tsx";
import { login } from "./store/auth.tsx";
import { fetchExamples } from "./store/example.tsx";
import {
  EconomicConnect,
  useEconomicIntegration,
} from "./components/integration";
import ConversationContainer from "./components/conversation/ConversationContainer.tsx";
import { useTypedSelector } from "./hooks.tsx";
import handleSignupToken from "./components/search/signup/handleSignupToken.tsx";
import { loadCatalog } from "./i18n.ts";
import { i18n } from "@lingui/core";
import axios from "./utils/axios.tsx";

import Overview from "./pages/Overview";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import handleInitiateSignUp from "./components/search/signup/handleInitiateSignUp.tsx";
import { msg } from "@lingui/core/macro";

let isInitial: boolean = true;

function App() {
  const [localeReady, setLocaleReady] = useState(false);

  useLayoutEffect(() => {
    const initializeLocale = async () => {
      const browserLang = navigator.language.split("-")[0]; // can be en-US, da-DK...
      if (browserLang === "da") {
        await loadCatalog("da");
      } else {
        await loadCatalog("en");
      }
      setLocaleReady(true);
    };
    initializeLocale();
  }, []);

  const [loading, setLoading] = useState(true);
  const isAuthenticated = useTypedSelector(
    (state) => state.authState.isLoggedIn,
  );

  const loginToken = useTypedSelector((state) => state.authState.loginToken);
  const dispatch = useDispatch();
  const location = useLocation();
  const { shouldRenderEconomicConnect, isEconomicIntegrationPath } =
    useEconomicIntegration();
  const handleWelcomeConversationSelection = () => {
    dispatch(fetchConversations() as any);
    dispatch(fetchExamples({ isLoggedIn: true }) as any);

    const welcomeConversationId = localStorage.getItem(
      "welcome_conversation_identifier",
    );
    if (welcomeConversationId) {
      dispatch(
        conversationActions.setSelectedConversation(welcomeConversationId),
      );
      dispatch(fetchExchanges(welcomeConversationId) as any);
      return welcomeConversationId;
    } else {
      dispatch(conversationActions.setSelectedConversation("new"));
      return null;
    }
  };

  useEffect(() => {
    const updateLoginToken = () => {
      let token_from_storage = localStorage.getItem("token");
      if (token_from_storage === null || token_from_storage === "") {
        if (isInitial) {
          dispatch(fetchExamples({ isLoggedIn: false }) as any);
          isInitial = false;
        }
        setLoading(false);
        return false;
      }
      let setToken = !loginToken && token_from_storage;
      if (setToken) {
        dispatch(login(token_from_storage));
        // Fetch feature flags immediately after login
        dispatch(fetchFeatureFlags() as any);
      }
      setLoading(false);
      return setToken;
    };

    const fetchExchangesOnLogin = () => {
      if (!isInitial || !isAuthenticated) {
        return;
      }
      isInitial = false;
      handleWelcomeConversationSelection();
    };

    let setToken = updateLoginToken();
    if (!setToken) {
      fetchExchangesOnLogin();
    }
  }, [loginToken, dispatch]);

  const signUpToken = useTypedSelector(
    (state) => state.authState.anonymousSession?.signUpState?.signUpToken,
  );
  useEffect(() => {
    const signupToken = async () => {
      // Only call handleSignupToken if locale is ready
      if (localeReady && i18n.locale) {
        await handleSignupToken(signUpToken, dispatch);
      }
    };

    signupToken();
  }, [signUpToken, dispatch, localeReady]);

  useEffect(() => {
    // set anon user cookie - only if we don't have a token and are not authenticated
    const fetchAnonymousUserAuthCookie = async () => {
      const hasToken = localStorage.getItem("token");
      if (isAuthenticated || hasToken) {
        return;
      }
      try {
        const response = await axios.get("/v1/anonymous/session-cookie/", {
          withCredentials: true, // send cookies
        });
        console.log("Cookie check:", response.data);

        const welcomeConversationId = localStorage.getItem(
          "welcome_conversation_identifier",
        );
        if (!welcomeConversationId) {
          dispatch(conversationActions.setSelectedConversation("new"));
        }
      } catch (error) {
        console.error("Cookie setup failed:", error);
      }
    };
    fetchAnonymousUserAuthCookie();
  }, [isAuthenticated]);

  // Handle welcome conversation selection after e-conomic connection
  useEffect(() => {
    const shouldSelectWelcomeConversation = localStorage.getItem(
      "shouldSelectWelcomeConversation",
    );
    if (shouldSelectWelcomeConversation === "true" && isAuthenticated) {
      localStorage.removeItem("shouldSelectWelcomeConversation");
      handleWelcomeConversationSelection();
    }
  }, [isAuthenticated, location.pathname]);

  if (loading) {
    return <div>Loading...</div>; // or a spinner/loading component
  }

  // Render EconomicConnect if integration path is detected
  if (shouldRenderEconomicConnect) {
    // Connection to economic from asktally.com
    return (
      <ThemeProvider theme={mainTheme}>
        <CssBaseline />
        <EconomicConnect />
      </ThemeProvider>
    );
  } else if (isEconomicIntegrationPath) {
    // Connection from economic market-place
    localStorage.setItem("signUpFromEconomicMarketPlace", "true");
    window.location.href = "/";
    return null;
  }

  if (
    !isEconomicIntegrationPath &&
    localStorage.getItem("signUpFromEconomicMarketPlace")
  ) {
    if (localeReady && i18n.locale) {
      dispatch(conversationActions.setSelectedConversation("new"));
      handleInitiateSignUp(
        i18n
          ._(
            msg({
              message: "create an account",
              context: "Message initiating the signup flow",
            }),
          )
          .toLowerCase(),
        dispatch,
      );
      localStorage.removeItem("signUpFromEconomicMarketPlace");
    }
  }

  return (
    <ThemeProvider theme={mainTheme}>
      <CssBaseline />

      <Routes>
        <Route
          path="/"
          element={
            <Fragment>
              <Grid container direction="row" spacing={3} alignContent="center">
                <ConversationContainer />
              </Grid>
            </Fragment>
          }
        />
        <Route path="/policies/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/overview" element={<Overview />} />
        {/* Redirect all other routes to "/" */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ThemeProvider>
  );
}

export default App;
